import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { createProductionLog, updateProductionLog, getFlavors, addFlavor as addFlavorApi, generateNewBatchId } from '../../../services/productionLogService';

const KEG_GALLONS = 5;
const CAN_OZ = 12;
const GALLONS_TO_OZ = 128;

function calcYield(volumeGallons, tankBottomLoss, lossPercent, kegs, cans) {
  const bottom = parseFloat(tankBottomLoss) || 3;
  const loss = parseFloat(lossPercent) || 0;
  const total = parseFloat(volumeGallons) || 0;
  const usable = (total - bottom) * (1 - loss / 100);
  const usableOz = usable * GALLONS_TO_OZ;
  if (kegs !== '' && kegs !== undefined && kegs !== null) {
    const kegOz = (parseFloat(kegs) || 0) * KEG_GALLONS * GALLONS_TO_OZ;
    const remainingCans = Math.floor((usableOz - kegOz) / CAN_OZ);
    return { usableGallons: usable.toFixed(2), cans: remainingCans > 0 ? remainingCans : 0 };
  }
  if (cans !== '' && cans !== undefined && cans !== null) {
    const canOzTotal = (parseFloat(cans) || 0) * CAN_OZ;
    const remainingKegs = (usableOz - canOzTotal) / (KEG_GALLONS * GALLONS_TO_OZ);
    return { usableGallons: usable.toFixed(2), kegs: remainingKegs > 0 ? remainingKegs.toFixed(2) : 0 };
  }
  return { usableGallons: usable.toFixed(2) };
}

function emptyPHReading() {
  return { date: '', target: '', actual: '', notes: '', stage: '' };
}

function toDateInput(val) {
  if (!val) return '';
  const d = new Date(val);
  if (isNaN(d)) return val;
  return d.toISOString().slice(0, 10);
}

function emptyIngredient() {
  return { name: '', amount: '', unit: 'lbs' };
}

export default function ProductionLogForm({ category, categories, editRecord, onSaved, onCancel, allLogs, phTargets, getPHTarget }) {
  const { user } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState(category || 'kombucha');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [yieldCalc, setYieldCalc] = useState({});

  const [form, setForm] = useState({
    startDate: '',
    expirationDate: '',
    flavor: '',
    volumeGallons: '',
    tankBottomLoss: '3',
    lossPercent: '',
    kegs: '',
    cans: '',
    starterUsed: '',
    specificGravity: '',
    correctiveAction: '',
    qualityNotes: '',
    packDate: '',
    grainVolume: '',
    mixingPH: '',
    packagingPH: '',
    pH: '',
    batchWeightLbs: '',
    jarCount: '',
    otherFarmNotes: '',
    brineVolumeGallons: '',
    bottlingDate: '',
    bottleCount: '',
    parentBatchId: '',
    notes: ''
  });

  const [phReadings, setPhReadings] = useState([emptyPHReading()]);
  const [arIngredients, setArIngredients] = useState([emptyIngredient()]);
  const [flavorList, setFlavorList] = useState([]);
  const [generatedBatchId, setGeneratedBatchId] = useState('');
  const [batchIdLoading, setBatchIdLoading] = useState(false);

  useEffect(() => {
    if (editRecord) {
      setForm(f => ({ ...f, ...editRecord,
        startDate: toDateInput(editRecord.startDate),
        expirationDate: toDateInput(editRecord.expirationDate),
        packDate: toDateInput(editRecord.packDate),
        bottlingDate: toDateInput(editRecord.bottlingDate),
      }));
      setSelectedCategory(editRecord.category || category);
      if (editRecord.phReadings) {
        try { setPhReadings(JSON.parse(editRecord.phReadings)); } catch {}
      }
      if (editRecord.arIngredients) {
        try { setArIngredients(JSON.parse(editRecord.arIngredients)); } catch {}
      }
    }
  }, [editRecord]);

  useEffect(() => {
    loadFlavors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  async function loadFlavors() {
    const res = await getFlavors(selectedCategory);
    if (res.success) setFlavorList(res.data);
  }

  async function handleFlavorSelect(e) {
    const value = e.target.value;
    if (value === '__ADD_NEW__') {
      const newFlavor = window.prompt('Enter new flavor name:');
      if (!newFlavor || !newFlavor.trim()) return;
      const res = await addFlavorApi(selectedCategory, newFlavor.trim());
      if (res.success) {
        await loadFlavors();
        setForm(f => ({ ...f, flavor: newFlavor.trim() }));
        await regenerateBatchId(newFlavor.trim());
      } else {
        alert('Error adding flavor: ' + res.error);
      }
      return;
    }
    setForm(f => ({ ...f, flavor: value }));
    if (value) await regenerateBatchId(value);
  }

  async function regenerateBatchId(flavor) {
    if (!flavor) { setGeneratedBatchId(''); return; }
    setBatchIdLoading(true);
    const res = await generateNewBatchId(selectedCategory, flavor);
    setBatchIdLoading(false);
    if (res.success) setGeneratedBatchId(res.batchId);
  }

  useEffect(() => {
    if (['kombucha', 'waterKefir', 'switchel'].includes(selectedCategory)) {
      const result = calcYield(form.volumeGallons, form.tankBottomLoss, form.lossPercent,
        form.kegs !== '' ? form.kegs : null,
        form.cans !== '' ? form.cans : null);
      setYieldCalc(result);
    }
  }, [form.volumeGallons, form.tankBottomLoss, form.lossPercent, form.kegs, form.cans, selectedCategory]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  }

  function handleKegChange(e) {
    setForm(f => ({ ...f, kegs: e.target.value, cans: '' }));
  }

  function handleCanChange(e) {
    setForm(f => ({ ...f, cans: e.target.value, kegs: '' }));
  }

  // pH readings handlers
  function updatePHReading(index, field, value) {
    setPhReadings(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  }

  function addPHReading() {
    setPhReadings(prev => [...prev, emptyPHReading()]);
  }

  function removePHReading(index) {
    setPhReadings(prev => prev.filter((_, i) => i !== index));
  }

  // AR ingredients handlers
  function updateIngredient(index, field, value) {
    setArIngredients(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  }

  function addIngredient() {
    setArIngredients(prev => [...prev, emptyIngredient()]);
  }

  function removeIngredient(index) {
    setArIngredients(prev => prev.filter((_, i) => i !== index));
  }

  async function handleSubmit() {
    if (!form.startDate) return setError('Start date is required.');
    if (selectedCategory !== 'brines' && !form.flavor) return setError('Flavor is required.');
    setError('');
    setSaving(true);

    const data = {
      ...form,
      loggedBy: user?.name || user?.email || 'Unknown',
      kegs: yieldCalc.kegs !== undefined ? yieldCalc.kegs : form.kegs,
      cans: yieldCalc.cans !== undefined ? yieldCalc.cans : form.cans,
      phReadings: JSON.stringify(phReadings.filter(r => r.date || r.actual)),
      arIngredients: JSON.stringify(arIngredients.filter(r => r.name && r.amount)),
      preferredBatchId: generatedBatchId || undefined,
    };

    let res;
    if (editRecord) {
      res = await updateProductionLog(selectedCategory, editRecord.batchId, data);
    } else {
      res = await createProductionLog(selectedCategory, data);
    }

    setSaving(false);
    if (res.success) onSaved();
    else setError('Error saving: ' + res.error);
  }

  const vegBatches = allLogs.filter(l => l.category === 'fermentedVeg');
  const isBeverage = ['kombucha', 'waterKefir', 'switchel'].includes(selectedCategory);

  return (
    <div className="pl-form-container">
      <div className="pl-form-header">
        <h2>{editRecord ? 'Edit Batch' : 'New Batch'}</h2>
        <button className="pl-btn-secondary" onClick={onCancel}>← Back</button>
      </div>

      {error && <div className="pl-error">{error}</div>}

      {/* Category Selector */}
      {!editRecord && (
        <div className="pl-form-group">
          <label>Category</label>
          <div className="pl-category-tabs">
            {categories.map(c => (
              <button key={c.key}
                className={`pl-tab ${selectedCategory === c.key ? 'active' : ''}`}
                onClick={() => setSelectedCategory(c.key)}>
                {c.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Shared Fields */}
      <div className="pl-form-row">
        <div className="pl-form-group">
          <label>Start Date</label>
          <input type="date" name="startDate" value={form.startDate} onChange={handleChange} />
        </div>
        <div className="pl-form-group">
          <label>Expiration / BBD</label>
          <input type="date" name="expirationDate" value={form.expirationDate} onChange={handleChange} />
        </div>
      </div>

      {selectedCategory !== 'brines' && (
        <div className="pl-form-group">
          <label>Flavor / Variety</label>
          <select value={form.flavor} onChange={handleFlavorSelect}>
            <option value="">Select flavor...</option>
            {flavorList.map((f, i) => (
              <option key={i} value={f.flavor}>{f.flavor}</option>
            ))}
            <option value="__ADD_NEW__">+ Add New Flavor</option>
          </select>
          {form.flavor && (
            <div className="pl-batch-id-preview">
              {batchIdLoading ? 'Generating batch ID...' : generatedBatchId ? `Batch ID: ${generatedBatchId}` : ''}
            </div>
          )}
        </div>
      )}

      {/* Beverage Volume + Yield */}
      {isBeverage && (
        <>
          <div className="pl-form-group">
            <label>Batch Volume (gallons)</label>
            <input type="number" name="volumeGallons" value={form.volumeGallons} onChange={handleChange} placeholder="e.g. 80" />
          </div>
          <div className="pl-form-section">
            <div className="pl-form-section-title">Yield Calculator</div>
            <div className="pl-form-row">
              <div className="pl-form-group">
                <label>Tank Bottom Loss (gal)</label>
                <input type="number" name="tankBottomLoss" value={form.tankBottomLoss} onChange={handleChange} />
              </div>
              <div className="pl-form-group">
                <label>Loss % (spillage)</label>
                <input type="number" name="lossPercent" value={form.lossPercent} onChange={handleChange} placeholder="e.g. 3" />
              </div>
            </div>
            {yieldCalc.usableGallons && (
              <div className="pl-yield-info">
                Usable: <strong>{yieldCalc.usableGallons} gal</strong>
                &nbsp;(max {Math.floor(parseFloat(yieldCalc.usableGallons) * GALLONS_TO_OZ / CAN_OZ)} cans or {(parseFloat(yieldCalc.usableGallons) / KEG_GALLONS).toFixed(2)} kegs)
              </div>
            )}
            <div className="pl-form-row">
              <div className="pl-form-group">
                <label>Kegs</label>
                <input type="number" name="kegs" value={form.kegs} onChange={handleKegChange} placeholder="Enter kegs..." />
                {yieldCalc.cans !== undefined && form.kegs !== '' && (
                  <div className="pl-yield-result">→ {yieldCalc.cans} cans remaining</div>
                )}
              </div>
              <div className="pl-form-group">
                <label>Cans (12 oz)</label>
                <input type="number" name="cans" value={form.cans} onChange={handleCanChange} placeholder="Enter cans..." />
                {yieldCalc.kegs !== undefined && form.cans !== '' && (
                  <div className="pl-yield-result">→ {yieldCalc.kegs} kegs remaining</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Kombucha specific */}
      {selectedCategory === 'kombucha' && (
        <>
          <div className="pl-form-section">
            <div className="pl-form-section-title">Batch Details</div>
            <div className="pl-form-row">
              <div className="pl-form-group">
                <label>Starter Used</label>
                <input type="text" name="starterUsed" value={form.starterUsed} onChange={handleChange} placeholder="e.g. SCOBY batch #3" />
              </div>
              <div className="pl-form-group">
                <label>Specific Gravity</label>
                <input type="number" step="0.001" name="specificGravity" value={form.specificGravity} onChange={handleChange} placeholder="e.g. 1.020" />
              </div>
            </div>
          </div>

          <div className="pl-form-section">
            <div className="pl-form-section-title">pH Readings</div>
            {phReadings.map((reading, i) => {
              const isLast = i === phReadings.length - 1;
              const target = isLast ? getPHTarget('kombucha', 'finalPH') : '';
              return (
              <div key={i} className="pl-ph-row">
                <div className="pl-ph-row-header">
                  <span>{isLast ? 'Final Reading' : `Reading ${i + 1}`}</span>
                  {phReadings.length > 1 && (
                    <button className="pl-btn-remove" onClick={() => removePHReading(i)}>✕</button>
                  )}
                </div>
                <div className="pl-form-row">
                  <div className="pl-form-group">
                    <label>Date</label>
                    <input type="date" value={reading.date} onChange={e => updatePHReading(i, 'date', e.target.value)} />
                  </div>
                  <div className="pl-form-group">
                    <label>Actual pH</label>
                    <input type="number" step="0.1" value={reading.actual} onChange={e => updatePHReading(i, 'actual', e.target.value)} placeholder={target ? `Target: ${target}` : 'e.g. 3.4'} />
                  </div>
                </div>
                <div className="pl-form-group">
                  <label>Notes</label>
                  <input type="text" value={reading.notes} onChange={e => updatePHReading(i, 'notes', e.target.value)} placeholder="e.g. Needs more time" />
                </div>
              </div>
              );
            })}
            <button className="pl-btn-add" onClick={addPHReading}>+ Add pH Reading</button>
          </div>

          <div className="pl-form-section">
            <div className="pl-form-section-title">Quality & Packaging</div>
            <div className="pl-form-group">
              <label>Corrective Action Needed</label>
              <input type="text" name="correctiveAction" value={form.correctiveAction} onChange={handleChange} placeholder="e.g. Extended fermentation 2 days" />
            </div>
            <div className="pl-form-group">
              <label>Quality Notes</label>
              <input type="text" name="qualityNotes" value={form.qualityNotes} onChange={handleChange} placeholder="e.g. Great color, strong ginger notes" />
            </div>
            <div className="pl-form-group">
              <label>Pack Date</label>
              <input type="date" name="packDate" value={form.packDate} onChange={handleChange} />
            </div>
          </div>
        </>
      )}

      {/* Water Kefir specific */}
      {selectedCategory === 'waterKefir' && (
        <>
          <div className="pl-form-section">
            <div className="pl-form-section-title">Primary Fermentation</div>
            <div className="pl-form-row">
              <div className="pl-form-group">
                <label>Kefir Grain Volume</label>
                <input type="text" name="grainVolume" value={form.grainVolume} onChange={handleChange} placeholder="e.g. 2 cups" />
              </div>
              <div className="pl-form-group">
                <label>Specific Gravity</label>
                <input type="number" step="0.001" name="specificGravity" value={form.specificGravity} onChange={handleChange} placeholder="e.g. 1.020" />
              </div>
            </div>
          </div>

          <div className="pl-form-section">
            <div className="pl-form-section-title">pH Readings</div>
            {phReadings.map((reading, i) => {
              const isMixDay = i === 0;
              const stage = isMixDay ? 'mix' : (reading.stage || 'primary');
              const target = stage === 'primary' ? getPHTarget('waterKefir', 'primaryPH')
                : stage === 'secondary' ? getPHTarget('waterKefir', 'secondaryPH') : '';
              return (
              <div key={i} className="pl-ph-row">
                <div className="pl-ph-row-header">
                  <span>{isMixDay ? 'Mix Day' : stage === 'primary' ? 'Primary Fermentation' : 'Secondary Fermentation'}</span>
                  {phReadings.length > 1 && (
                    <button className="pl-btn-remove" onClick={() => removePHReading(i)}>✕</button>
                  )}
                </div>
                <div className="pl-form-row">
                  <div className="pl-form-group">
                    <label>Date</label>
                    <input type="date" value={reading.date} onChange={e => updatePHReading(i, 'date', e.target.value)} />
                  </div>
                  {!isMixDay && (
                    <div className="pl-form-group">
                      <label>Stage</label>
                      <select value={stage} onChange={e => updatePHReading(i, 'stage', e.target.value)}>
                        <option value="primary">Primary</option>
                        <option value="secondary">Secondary</option>
                      </select>
                    </div>
                  )}
                  <div className="pl-form-group">
                    <label>Actual pH</label>
                    <input type="number" step="0.1" value={reading.actual} onChange={e => updatePHReading(i, 'actual', e.target.value)} placeholder={target ? `Target: ${target}` : 'e.g. 4.5'} />
                  </div>
                </div>
                <div className="pl-form-group">
                  <label>Notes</label>
                  <input type="text" value={reading.notes} onChange={e => updatePHReading(i, 'notes', e.target.value)} placeholder="e.g. Added to secondary" />
                </div>
              </div>
              );
            })}
            <button className="pl-btn-add" onClick={addPHReading}>+ Add pH Reading</button>
          </div>

          <div className="pl-form-section">
            <div className="pl-form-section-title">🌱 AR Farm Ingredients</div>
            {arIngredients.map((ing, i) => (
              <div key={i} className="pl-ingredient-row">
                <div className="pl-form-row">
                  <div className="pl-form-group" style={{flex: 2}}>
                    <label>Ingredient</label>
                    <input type="text" value={ing.name} onChange={e => updateIngredient(i, 'name', e.target.value)} placeholder="e.g. Blueberries" />
                  </div>
                  <div className="pl-form-group">
                    <label>Amount</label>
                    <input type="number" value={ing.amount} onChange={e => updateIngredient(i, 'amount', e.target.value)} placeholder="10" />
                  </div>
                  <div className="pl-form-group">
                    <label>Unit</label>
                    <select value={ing.unit} onChange={e => updateIngredient(i, 'unit', e.target.value)}>
                      <option value="lbs">lbs</option>
                      <option value="oz">oz</option>
                      <option value="cups">cups</option>
                      <option value="quarts">quarts</option>
                      <option value="gallons">gallons</option>
                    </select>
                  </div>
                  {arIngredients.length > 1 && (
                    <button className="pl-btn-remove" style={{alignSelf:'flex-end', marginBottom: '16px'}} onClick={() => removeIngredient(i)}>✕</button>
                  )}
                </div>
              </div>
            ))}
            <button className="pl-btn-add" onClick={addIngredient}>+ Add Ingredient</button>
          </div>

          <div className="pl-form-section">
            <div className="pl-form-section-title">Quality & Packaging</div>
            <div className="pl-form-group">
              <label>Quality Notes</label>
              <input type="text" name="qualityNotes" value={form.qualityNotes} onChange={handleChange} placeholder="e.g. Great effervescence" />
            </div>
            <div className="pl-form-group">
              <label>Pack Date</label>
              <input type="date" name="packDate" value={form.packDate} onChange={handleChange} />
            </div>
          </div>
        </>
      )}

      {/* Switchel */}
      {selectedCategory === 'switchel' && (
        <div className="pl-form-section">
          <div className="pl-form-section-title">pH Reading</div>
          <div className="pl-form-group">
            <label>pH After Mixing</label>
            <input type="number" step="0.1" name="mixingPH" value={form.mixingPH} onChange={handleChange} placeholder="e.g. 3.5" />
          </div>
          <div className="pl-form-group">
            <label>Pack Date</label>
            <input type="date" name="packDate" value={form.packDate} onChange={handleChange} />
          </div>
        </div>
      )}

      {/* Fermented Veg */}
      {selectedCategory === 'fermentedVeg' && (
        <>
          <div className="pl-form-group">
            <label>Batch Size (lbs)</label>
            <input type="number" name="batchWeightLbs" value={form.batchWeightLbs} onChange={handleChange} placeholder="e.g. 50" />
          </div>

          <div className="pl-form-section">
            <div className="pl-form-section-title">🌱 AR Vegetables Used</div>
            {arIngredients.map((ing, i) => (
              <div key={i} className="pl-ingredient-row">
                <div className="pl-form-row">
                  <div className="pl-form-group" style={{flex: 2}}>
                    <label>Vegetable</label>
                    <input type="text" value={ing.name} onChange={e => updateIngredient(i, 'name', e.target.value)} placeholder="e.g. Cabbage" />
                  </div>
                  <div className="pl-form-group">
                    <label>Amount</label>
                    <input type="number" value={ing.amount} onChange={e => updateIngredient(i, 'amount', e.target.value)} placeholder="30" />
                  </div>
                  <div className="pl-form-group">
                    <label>Unit</label>
                    <select value={ing.unit} onChange={e => updateIngredient(i, 'unit', e.target.value)}>
                      <option value="lbs">lbs</option>
                      <option value="oz">oz</option>
                    </select>
                  </div>
                  {arIngredients.length > 1 && (
                    <button className="pl-btn-remove" style={{alignSelf:'flex-end', marginBottom: '16px'}} onClick={() => removeIngredient(i)}>✕</button>
                  )}
                </div>
              </div>
            ))}
            <button className="pl-btn-add" onClick={addIngredient}>+ Add Vegetable</button>
          </div>

          <div className="pl-form-group">
            <label>Other Farm Sourcing Notes</label>
            <input type="text" name="otherFarmNotes" value={form.otherFarmNotes} onChange={handleChange} placeholder="e.g. 20 lbs cabbage from Green Acres Farm" />
          </div>
          <div className="pl-form-row">
            <div className="pl-form-group">
              <label>pH on Packaging Day</label>
              <input type="number" step="0.1" name="packagingPH" value={form.packagingPH} onChange={handleChange} placeholder="e.g. 3.4" />
            </div>
            <div className="pl-form-group">
              <label>Number of Jars Filled</label>
              <input type="number" name="jarCount" value={form.jarCount} onChange={handleChange} placeholder="e.g. 48" />
            </div>
          </div>
          <div className="pl-form-group">
            <label>Pack Date</label>
            <input type="date" name="packDate" value={form.packDate} onChange={handleChange} />
          </div>
        </>
      )}

      {/* Brines */}
      {selectedCategory === 'brines' && (
        <>
          <div className="pl-form-group">
            <label>Parent Vegetable Batch</label>
            <select name="parentBatchId" value={form.parentBatchId} onChange={handleChange}>
              <option value="">Select parent batch...</option>
              {vegBatches.map((b, i) => (
                <option key={i} value={b.batchId}>{b.flavor} — Batch {b.batchId}</option>
              ))}
            </select>
          </div>
          {form.parentBatchId && (
            <div className="pl-form-group">
              <label>Flavor (from parent batch)</label>
              <input type="text" name="flavor" value={form.flavor || vegBatches.find(b => b.batchId === form.parentBatchId)?.flavor || ''} onChange={handleChange} />
            </div>
          )}
          <div className="pl-form-group">
            <label>Brine Volume Yielded (gallons)</label>
            <input type="number" name="brineVolumeGallons" value={form.brineVolumeGallons} onChange={handleChange} placeholder="e.g. 3" />
          </div>
          <div className="pl-form-row">
            <div className="pl-form-group">
              <label>Bottling Date</label>
              <input type="date" name="bottlingDate" value={form.bottlingDate} onChange={handleChange} />
            </div>
            <div className="pl-form-group">
              <label>pH</label>
              <input type="number" step="0.1" name="pH" value={form.pH} onChange={handleChange} placeholder="e.g. 3.2" />
            </div>
          </div>
          <div className="pl-form-group">
            <label>Number of Bottles Filled</label>
            <input type="number" name="bottleCount" value={form.bottleCount} onChange={handleChange} placeholder="e.g. 24" />
          </div>
        </>
      )}

      {/* Notes */}
      <div className="pl-form-group">
        <label>Notes</label>
        <textarea name="notes" value={form.notes} onChange={handleChange} placeholder="Any additional notes..." rows={3} />
      </div>

      <div className="pl-form-actions">
        <button className="pl-btn-secondary" onClick={onCancel}>Cancel</button>
        <button className="pl-btn-primary" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving...' : editRecord ? 'Update Batch' : 'Save Batch'}
        </button>
      </div>
    </div>
  );
}
